/* eslint-disable react-hooks/immutability, react-hooks/preserve-manual-memoization, react-hooks/set-state-in-effect -- this legacy select keeps touch bookkeeping and controlled focus synchronization outside React Compiler assumptions. */
import {css, styled} from "styled-components";
import type {CSSProperties, FunctionComponent, MouseEvent, ReactNode, SVGProps, TouchEvent} from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {colorPalette} from "../../styles/colorPalette";
import {ELLIPSIS, FLEXCENTER, FlexCenterStyle, VerticalFlexStyle, baseDistance, fontSize, radius, zIndex} from "../../styles/styles";
import type {TransformObj} from "../../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import {AppUtils} from "../../../../utils/AppUtils";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {useAppStore} from "../../../../StateManager";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {ReactUtils} from "../../../utils/ReactUtils";
import {SearchFieldV5} from "../search/SearchFieldV5";
import ChevronDownIcon from "../../icons/chevron-down.svg?react";
import {DomPortal} from "../../../modules/abstract/portal/DomPortal";
import {useClickOutside, useCloseOnKeyboardPress} from "../../utils";
import CloseIcon from "../../icons/xmark.svg?react";
import {ColorUtils} from "../../../../utils/ColorUtils";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {SelectInputStyledClass} from "../../popup/SelectInputConstants";
import {ObjectUtils} from "../../../../utils/data/ObjectUtils";
import type {DisplayFormat} from "../../details/ListBuilderInputV5";
import {stringifySelectOption} from "./SelectInputUtils";

export const PeopleFieldClassName = "peopleField";

interface ISelectInputPropsV5<T> {
	readonly optionStyle?: React.CSSProperties;
	readonly className?: string;
	readonly childBeforeOptions?: ReactNode;
	readonly options: readonly T[];
	readonly nullOption?: boolean;
	readonly selected?: T;
	readonly type?: string;
	readonly disabled?: boolean;
	readonly runOnClickEvenWhenDisabled?: boolean;
	readonly nullLabel?: string;
	readonly fullWidth?: boolean;
	readonly searchBarFullWidth?: boolean;
	readonly dropdownFixedWidth?: string;
	readonly dropdownMaxHeight?: string;
	readonly sort?: boolean;
	readonly searchBarMode?: "always on" | "always off" | "when many items";
	readonly noFixedPosition?: boolean;
	readonly optionsZIndex?: number;
	readonly placeholder?: string;
	readonly focused?: boolean;
	readonly IconComponent?: FunctionComponent<SVGProps<SVGSVGElement> & {title?: string}>;
	readonly dropdownIcon?: boolean;
	readonly isSameWidth?: boolean;
	readonly renderOptionWhenClosed?: (item: T) => React.ReactNode;
	readonly renderOptionWhenOpen?: (item: T) => React.ReactNode; // if not provided, it uses renderOptionWhenClosed
	readonly stringifyOption?: (item: T) => string;
	readonly onChange?: (option: T) => void;
	readonly onFocus?: (value: boolean) => void;
	readonly onClick?: (e: React.MouseEvent) => void;
	readonly onClose?: () => void;
	readonly horizontalAlignment?: HorizontalAlignment;
	readonly verticalAlignment?: VerticalAlignment;
	readonly offsetX?: number;
	readonly offsetY?: number;
	readonly isConfirmXyiconModelChangesForm?: boolean;
	readonly inline?: boolean;
	readonly isInherited?: boolean;
	readonly usePillStyle?: boolean;
	readonly closeIcon?: boolean;
	readonly isFixedWidth?: boolean;
	readonly optionFontSize?: string;
	readonly selectedIsRemoved?: boolean;
	readonly dropdownOnly?: boolean;
}

export const SelectInputV5 = <T,>({
	optionStyle = {},
	options = [],
	nullOption = false,
	disabled = false,
	runOnClickEvenWhenDisabled = false,
	stringifyOption = stringifySelectOption,
	renderOptionWhenClosed = (option: any) => <>{stringifyOption(option)}</>,
	renderOptionWhenOpen = renderOptionWhenClosed,
	nullLabel = "",
	sort = true,
	searchBarMode = "when many items",
	IconComponent,
	dropdownIcon = true,
	onClick,
	onFocus,
	onClose,
	focused,
	onChange,
	selected,
	childBeforeOptions,
	noFixedPosition,
	optionsZIndex,
	className,
	fullWidth,
	searchBarFullWidth,
	dropdownFixedWidth,
	dropdownMaxHeight,
	placeholder,
	isSameWidth = false,
	verticalAlignment = VerticalAlignment.bottomOuter,
	horizontalAlignment = HorizontalAlignment.left,
	offsetY = 8,
	offsetX = 0,
	isConfirmXyiconModelChangesForm = false,
	inline,
	isInherited = false,
	usePillStyle = false,
	type,
	closeIcon,
	isFixedWidth,
	optionFontSize,
	selectedIsRemoved,
	dropdownOnly = false,
}: ISelectInputPropsV5<T>) => {
	const _element = useRef<HTMLDivElement>(null);
	const _list = useRef<HTMLDivElement>(null);
	const _searchBar = useRef<HTMLInputElement>(null);
	const _prevFocused = useRef<boolean>(false);
	let _touchMove = false;

	const appState = useAppStore((state) => state.appState);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");
	const [transform, setTransform] = useState<TransformObj>(null);

	const placeholderProp = placeholder;

	useEffect(() => {
		if (_element.current && _list.current && isOpen) {
			const elementWidth = _element.current.offsetWidth;
			const listWidth = _list.current.offsetWidth;

			const effectiveHorizontalAlignment =
				horizontalAlignment === HorizontalAlignment.left && elementWidth < listWidth ? HorizontalAlignment.right : horizontalAlignment;

			setTransform(
				DomUtils.getFixedFloatingElementPosition(
					_element.current,
					_list.current,
					verticalAlignment,
					effectiveHorizontalAlignment,
					offsetY,
					offsetX,
					true,
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [_element.current, _list.current, verticalAlignment, horizontalAlignment, offsetY, offsetX, setTransform, isOpen, disabled]);

	useEffect(() => {
		if (focused && !_prevFocused.current) {
			if (!disabled) {
				setIsOpen(true);
			}
		} else {
			setIsOpen(false);
		}

		_prevFocused.current = focused;
	}, [focused, disabled]);

	const onOpen = useCallback(
		(event: MouseEvent) => {
			const isOpenCallback = async (event?: MouseEvent) => {
				if (!isOpen) {
					try {
						onClick?.(event);
						event?.stopPropagation();
						onFocus?.(true);
						setIsOpen(true);
						AppUtils.disableScrolling(true);

						await TimeUtils.wait(100);

						// Auto-scroll to selected option when isOpening
						// requestAnimationFrame is needed because the div is not visible immediately after calling setState(isOpen)
						requestAnimationFrame(() => {
							if (options.includes(selected)) {
								const selectedOption = _list.current?.querySelector(".Options.selected") as HTMLDivElement;

								if (!!selectedOption) {
									DomUtils.scrollIntoViewIfNeeded(selectedOption);
								}
							} else {
								// If the options are numbers, look for the closest one and scroll there.
								if (options.every((option) => typeof option === "number") && typeof selected === "number") {
									const closestOption = options.find((v) => v >= selected) ?? options[options.length - 1];
									const closestOptionElement = (Array.from(_list.current?.querySelectorAll(".Options")) as HTMLDivElement[]).find(
										(element) => element.title == closestOption.toString(),
									);

									if (!!closestOptionElement) {
										DomUtils.scrollIntoViewIfNeeded(closestOptionElement);
									}
								}
							}
						});
					} catch (error) {
						console.warn(error);
					}
				}
			};

			isOpenCallback(event);
		},
		[onFocus, onClick, isOpen, selected, options],
	);

	const onCloseMethod = useCallback(() => {
		if (isOpen) {
			AppUtils.disableScrolling(false);
			setIsOpen(false);
			setSearch("");
			onFocus?.(false);
			onClose?.();
		}
	}, [isOpen, onFocus, onClose]);

	const onToggle = (event: MouseEvent) => {
		if (disabled && !runOnClickEvenWhenDisabled) {
			return;
		} else if (runOnClickEvenWhenDisabled) {
			onClick?.(event);
			return;
		} else {
			if (_searchBar.current && (event.target as Element).contains(_searchBar.current)) {
				return;
			}

			if (isOpen) {
				onCloseMethod();
			} else {
				onOpen(event);
			}
		}
	};

	useClickOutside([_element, _list], onCloseMethod, "mouseup", [], false, isOpen);
	useCloseOnKeyboardPress(onCloseMethod, KeyboardListener.KEY_ESCAPE, [isOpen], {
		enabled: isOpen,
		eventType: "keydown",
		onlyLatest: true,
		stopPropagation: true,
	});
	useCloseOnKeyboardPress(onCloseMethod, KeyboardListener.KEY_ENTER, [isOpen], {enabled: isOpen});

	const onOptionTouchMove = () => {
		_touchMove = true;
	};

	const onOptionTouchEnd = (e: TouchEvent, option: T = null) => {
		if (!_touchMove) {
			e.nativeEvent.stopImmediatePropagation();
			AppUtils.disableScrolling(false);
			setIsOpen(false);
			setSearch("");
			onChange?.(option);
		}

		_touchMove = false;
	};

	const onOptionClick = (e: MouseEvent, option: T = null) => {
		e.nativeEvent.stopImmediatePropagation();
		AppUtils.disableScrolling(false);
		setIsOpen(false);
		setSearch("");
		onChange?.(option);
	};

	const renderLabel = (option: T, isPlaceholder: boolean) => {
		if (isPlaceholder && selected == null) {
			return <div className={ReactUtils.cls("placeholder", {flexCenter: placeholderProp === "-"})}>{placeholderProp}</div>;
		}

		if (!option && option !== 0) {
			if (inline) {
				return <div className="nullLabel">-</div>;
			}
			return nullOption ? <div className="nullLabel">{nullLabel}</div> : placeholder;
		} else {
			return ReactUtils.getTextContent(renderOptionWhenClosed(option) as React.ReactElement) || "";
		}
	};

	const clearSelected = () => {
		onChange?.(null);
	};

	const inlineStyle: CSSProperties = _list.current && {
		maxWidth: isSameWidth ? _element.current.offsetWidth : (dropdownFixedWidth ?? "500px"),
		minWidth: isSameWidth ? _element.current.offsetWidth : (dropdownFixedWidth ?? "150px"),
		transform: noFixedPosition ? "" : transform?.translate,
		position: noFixedPosition ? "absolute" : "fixed",
		zIndex: optionsZIndex ?? zIndex.contextOptions,
		visibility: "visible",
		top: 0,
		left: 0,
	};

	const selectedOption = selected ? (
		<SpanWithFlex1>{renderOptionWhenClosed(selected)}</SpanWithFlex1>
	) : (
		<SpanWithFlex1>{renderLabel(selected, true)}</SpanWithFlex1>
	);

	let optionElements: ReactNode[] = [];

	// Closed cells only need their selected label. Build fresh options on opening so in-place edits remain visible.
	if (isOpen) {
		const optionsClone = options.slice(); // Copy before sorting MobX observable arrays.

		if (sort) {
			optionsClone.sort((optionA, optionB) => {
				const a = stringifyOption ? stringifyOption(optionA) : renderLabel(optionA, false).toString();
				const b = stringifyOption ? stringifyOption(optionB) : renderLabel(optionB, false).toString();

				return StringUtils.sortIgnoreCase(a, b);
			});
		}

		optionElements = optionsClone
			.filter((option) => {
				let o: any = option;

				if (stringifyOption) {
					o = stringifyOption(option);
				}

				if (search) {
					const value = typeof o === "string" ? o : renderLabel(o, false).toString();

					return StringUtils.containsIgnoreCase(value, search);
				} else {
					return true;
				}
			})
			.map((option, index) => {
				const isSelected =
					typeof option === "object" && Object.hasOwn(option, "id")
						? (option as any).id === (selected as any)?.id
						: ObjectUtils.compare(option, selected);

				return (
					<OptionStyled
						title={stringifyOption(option)}
						key={`${index}_${stringifyOption(option)}`}
						className={ReactUtils.cls("Options", {
							selected: isSelected,
							removed: isSelected && selectedIsRemoved,
							usePillStyle,
						})}
						style={optionStyle}
						onMouseDown={(event) => {
							onOptionClick(event, option);
						}}
						onTouchMove={onOptionTouchMove}
						onTouchEnd={(event) => onOptionTouchEnd(event, option)}
						$optionFontSize={optionFontSize}
						$type={type ?? ""}
					>
						<SpanWithFlex1>{renderOptionWhenOpen(option)}</SpanWithFlex1>
					</OptionStyled>
				);
			});
	}

	return (
		<SelectInputStyled
			ref={_element}
			className={ReactUtils.cls(`${SelectInputStyledClass} ${className || ""}`, {
				disabled: disabled && !runOnClickEvenWhenDisabled,
				fullWidth,
				isOpen,
				usePillStyle,
				isInherited,
			})}
			onClick={onToggle}
			$inline={inline}
			$isOpen={isOpen}
			$usePillStyle={usePillStyle}
			$fixedWidth={isFixedWidth}
			$dropdownOnly={dropdownOnly}
			title={stringifyOption(selected)}
		>
			<div className="selected-option">
				{IconComponent && <IconComponent />}
				{!dropdownOnly && selectedOption}
				{dropdownIcon && <ChevronDownIcon className="dropdown-icon" />}
			</div>
			{!inline && closeIcon && selected && (
				<CloseIcon
					className="closeBtn"
					onClick={clearSelected}
				/>
			)}
			{isOpen && (
				<DomPortal
					destination={appState.app.modalContainer}
					noPortal={noFixedPosition}
				>
					<SelectInputDropdownStyled
						className={ReactUtils.cls("SelectInputDropdownClass", {usePillStyle})}
						style={inlineStyle}
						ref={_list}
						$isConfirmXyiconModelChangesForm={isConfirmXyiconModelChangesForm}
						$numberOfOptions={options.length}
					>
						{(searchBarMode === "always on" || (searchBarMode === "when many items" && options.length > 5)) && (
							<SearchWrapper>
								<SearchFieldV5
									value={search}
									onInput={(value) => setSearch(value)}
									divRef={_searchBar}
									fullWidth={searchBarFullWidth}
								/>
							</SearchWrapper>
						)}
						<ListElementContainerStyled style={{maxHeight: dropdownMaxHeight ?? "140px"}}>
							{childBeforeOptions}
							{nullOption && optionElements.length > 0 && !search && (
								<OptionStyled
									className={ReactUtils.cls("Options", {
										selected: !selected, // looks a bit weird but it's correct, because null option means selected is null/undefined, so in this case the null option is the selected one
										usePillStyle,
									})}
									style={optionStyle}
									onMouseDown={(event) => {
										onOptionClick(event);
									}}
									onTouchMove={onOptionTouchMove}
									onTouchEnd={onOptionTouchEnd}
									$type={type ? type : ""}
									$optionFontSize={optionFontSize}
								>
									{renderLabel(null, false)}
								</OptionStyled>
							)}
							{optionElements.length === 0 ? (
								<div className="bottom-section noValues">
									No values found.
									<br />
									{!search && "Contact your admin."}
								</div>
							) : (
								optionElements
							)}
						</ListElementContainerStyled>
					</SelectInputDropdownStyled>
				</DomPortal>
			)}
		</SelectInputStyled>
	);
};

const svgWidth = "18px";

const getBorder = ({$inline, $isOpen}: {$inline?: boolean; $isOpen?: boolean}) => {
	if ($inline && $isOpen) {
		return `2px solid ${colorPalette.primary.c500Primary}`;
	}
	if ($inline && !$isOpen) {
		return `2px solid transparent`;
	}
	if (!$inline && $isOpen) {
		return `1px solid ${colorPalette.primary.c500Primary}`;
	}
	return `1px solid ${colorPalette.gray.c300}`;
};

export const SelectInputStyled = styled.div<{
	$inline?: boolean;
	$isOpen?: boolean;
	$isSingleSelect?: boolean;
	$usePillStyle?: boolean;
	$fixedWidth?: boolean;
	$dropdownOnly?: boolean;
}>`
	cursor: pointer;
	${FlexCenterStyle};
	justify-content: space-between;
	gap: ${baseDistance.sm};
	height: ${(props) => (props.$usePillStyle && props.$inline ? "40px" : "32px")};
	padding-left: ${(props) => (props.$usePillStyle ? "0px" : baseDistance.sm)};
	padding-right: ${(props) => (props.$usePillStyle ? "0px" : baseDistance.sm)};
	border-radius: ${(props) => (props.$usePillStyle ? radius.xs : radius.sm)};
	border: ${getBorder};

	font-size: ${fontSize.md};
	line-height: ${fontSize.lg};
	width: ${(props) => (props.$fixedWidth ? "125px" : "")};

	&.isInherited {
		color: ${colorPalette.gray.c700Dark};
	}

	${(props) =>
		props.$dropdownOnly &&
		css`
			border: none;
			padding: ${baseDistance.xs};
			height: 20px;
		`}

	${(props) =>
		props.$inline &&
		props.$usePillStyle &&
		css`
			box-sizing: content-box;
		`}

	.closeBtn {
		visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
		position: absolute;
		right: 55px;
		right: 6px;
	}

	.selected-option {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		height: 100%;
		gap: ${baseDistance.sm};

		> span {
			white-space: nowrap;
			overflow: ${(props) => (props.$usePillStyle ? "visible" : "hidden")};
			text-overflow: ellipsis;
		}

		.dropdown-icon {
			width: 12px;
			height: 12px;
		}
	}

	& > span {
		display: flex;
		align-items: center;
		height: 100%;
		white-space: nowrap;
		overflow: ${(props) => (props.$usePillStyle ? "visible" : "hidden")};
		text-overflow: ellipsis;
		width: 100%;
	}

	&:hover {
		background-color: ${(props) => (props.$usePillStyle ? "" : colorPalette.gray.c200Light)};

		.closeBtn {
			visibility: visible;
			position: absolute;
			right: 6px;
		}
	}

	svg {
		width: ${svgWidth};
		height: ${svgWidth};
	}

	.empty {
		width: calc(100% - ${svgWidth});
		text-align: center;
	}

	.input {
		position: relative;
		padding-right: 30px;
		font-size: ${fontSize.md};
		flex: 1;
		line-height: 14px;

		.fieldOption {
			.linkIcon {
				right: 20px;
			}
		}

		svg {
			width: 12px;
			min-width: 12px;
			height: 12px;
			min-height: 12px;
			position: absolute;
			right: 5px;
			top: 2px;

			path {
				width: 6px;
				height: 3px;
			}
		}
	}

	.placeholder {
		color: ${colorPalette.gray.c500Primary};
	}
`;

export const PillStyled = styled.div<{$color?: string; $type?: DisplayFormat; $selected?: boolean; $inline?: boolean; $isMultiselect?: boolean}>`
	${FlexCenterStyle};
	cursor: pointer;
	font-size: ${fontSize.md};
	gap: ${baseDistance.xs};
	min-width: 0;

	${ELLIPSIS}

	svg {
		min-width: 16px;
		min-height: 16px;
	}

	${(props) => {
		const hslColor = ColorUtils.hex2hsl(props.$color);

		const nullLabelDefaultStyle = css`
			&.nullLabel {
				display: flex;
				padding: ${baseDistance.xs};
				background-color: transparent;
				color: ${colorPalette.black};
				min-height: 24px;
				width: 100%;
				text-align: left;
				align-items: center;
				justify-content: center;
				max-width: none;
			}
		`;

		switch (props.$type) {
			case "textOnly":
				return css`
					color: ${props.$color === colorPalette.white ? colorPalette.black : props.$color};
					margin: 0 ${baseDistance.xs};
					line-height: 16px;
					${nullLabelDefaultStyle}
				`;
			case "pillColored":
				const pillColoredCommonStyles = css`
					background-color: ${props.$color ? props.$color : "transparent"};
					border-radius: ${radius.xl};
					padding: ${baseDistance.xs};
					height: 24px;
					min-width: 32px;
					max-width: fit-content;
					margin: 0 ${baseDistance.xs};
					height: ${props.$selected ? "24px" : ""};
					text-align: center;
					line-height: 16px;
					justify-content: center;
					${nullLabelDefaultStyle}
				`;

				if (hslColor.l >= 0.83 || !props.$color || props.$color === "transparent") {
					return css`
						color: ${colorPalette.black};
						${pillColoredCommonStyles}
					`;
				} else {
					return css`
						color: ${colorPalette.white};
						${pillColoredCommonStyles}
					`;
				}
			case "pill":
				return css`
					border-radius: ${radius.xl};
					padding: ${baseDistance.xs};
					height: 24px;
					margin-left: ${baseDistance.xs};
					min-width: 32px;
					max-width: fit-content;
					margin-right: ${baseDistance.xs};
					background-color: ${colorPalette.gray.c100};
					color: ${props.$selected ? colorPalette.black : ""};
					${nullLabelDefaultStyle}

					svg {
						fill: ${props.$color ? props.$color : "transparent"};
						margin-right: ${baseDistance.xs};
						min-width: 11px;
						min-height: 11px;
						width: 11px;
						height: 11px;

						path {
							stroke: ${props.$color ? props.$color : "transparent"};
						}
					}
				`;
			case "flag":
				return css`
					margin: 0 ${baseDistance.xs};
					gap: ${baseDistance.xs};
					height: 16px;
					line-height: 16px;
					vertical-align: middle;
					${nullLabelDefaultStyle}

					svg {
						fill: ${props.$color ? props.$color : "transparent"};
						width: 16px;
						height: 16px;
						margin-right: ${baseDistance.xs};
						vertical-align: middle;

						path {
							stroke: ${props.$color ? props.$color : "transparent"};
						}
					}
				`;
			case "colorBar":
				return css`
					flex: 1;
					height: 40px;
					border-radius: ${baseDistance.xs};
					color: ${colorPalette.black};
					position: relative;
					padding-left: ${baseDistance.sm};
					line-height: ${props.$selected ? "24px" : "40px"};
					${nullLabelDefaultStyle}

					.colorline {
						position: absolute;
						background-color: ${props.$color ? props.$color : "transparent"};
						border-radius: ${radius.sm} 0px 0px ${radius.sm};
						height: ${props.$selected ? "100%" : "40px"};
						width: 4px;
						left: 0;
						top: 0;
					}
				`;
			case "colorBarFill":
				const colorAfterOpacity: string = ColorUtils.hex2rgb(props.$color, 0.3) as string;
				const rgbaToHex = ColorUtils.rgba2hex(colorAfterOpacity);
				const minimizedHslColor = ColorUtils.hex2hsl(rgbaToHex);

				const colorBarFilledCommonStyles = css`
					width: ${props.$selected && !props.$inline ? "fit-content" : "100%"};
					padding-right: ${props.$selected && !props.$inline ? baseDistance.sm : ""};
					height: 40px;
					border-radius: ${baseDistance.xs};
					background-color: ${colorAfterOpacity};
					position: relative;
					padding-left: ${baseDistance.sm};
					line-height: ${props.$selected ? "24px" : "40px"};
					${nullLabelDefaultStyle}

					.colorline {
						position: absolute;
						background-color: ${props.$color ? props.$color : "transparent"};
						border-radius: ${radius.sm} 0px 0px ${radius.sm};
						height: ${props.$selected ? "100%" : "40px"};
						width: 4px;
						left: 0;
						top: 0;
					}
				`;

				if (minimizedHslColor.l >= 0.83 || !props.$color || props.$color === "transparent") {
					return css`
						color: ${colorPalette.black};
						${colorBarFilledCommonStyles}
					`;
				} else {
					return css`
						color: ${colorPalette.white};
						${colorBarFilledCommonStyles}
					`;
				}
			default:
				const defaultcommonStyles = css`
					padding: ${props.$selected && !props.$inline ? "4px 8px" : baseDistance.sm};
					width: ${props.$selected && !props.$inline ? "fit-content" : "100%"};
					height: ${props.$selected && !props.$inline ? "24px" : "32px"};
					border-radius: ${radius.sm};
					background-color: ${props.$color ? props.$color : "transparent"};

					${
						props.$inline
							? css`
									&.nullLabel {
										background-color: ${colorPalette.white};
										color: ${colorPalette.black};
										border-radius: ${radius.sm};
										border: 1px solid ${colorPalette.gray.c300};
										height: 32px;
										${FLEXCENTER};
										width: 100%;
									}
								`
							: nullLabelDefaultStyle
					}

					${
						props.$isMultiselect
							? css`
									height: 24px;
									position: relative;

									.removeItem {
										display: none;
										right: 4px;
									}

									&:hover {
										.removeItem {
											display: block;
										}

										> span {
											padding-right: 20px;
										}
									}

									svg {
										max-width: 16px;
										height: 16px;
										fill: ${colorPalette.gray.c950};
										cursor: pointer;
									}
								`
							: nullLabelDefaultStyle
					}
				`;

				if (hslColor.l >= 0.83 || !props.$color || props.$color === "transparent") {
					return css`
						color: ${colorPalette.gray.c950};
						${defaultcommonStyles}
					`;
				} else {
					return css`
						color: ${colorPalette.white};
						${defaultcommonStyles}
					`;
				}
		}
	}}

	&.${PeopleFieldClassName} {
		color: inherit;
		background-color: inherit;
		padding: ${baseDistance.xs};
		box-sizing: border-box;
		justify-content: space-between;
		height: auto;
	}
`;

const SelectInputDropdownStyled = styled.div<{$isConfirmXyiconModelChangesForm: boolean; $numberOfOptions: number}>`
	${VerticalFlexStyle};
	visibility: hidden;
	background-color: ${colorPalette.white};
	border-radius: ${radius.sm};
	padding: ${baseDistance.sm};
	box-shadow: 0px ${baseDistance.xs} ${baseDistance.sm} 0px #00000033;

	svg.icon {
		width: ${baseDistance.md};
		height: ${baseDistance.md};
		margin-right: 5px;
	}

	.filterField {
		width: 100%;
		justify-content: space-between;
	}

	.SearchField {
		width: 99%;
	}

	${(props) =>
		props.$isConfirmXyiconModelChangesForm &&
		props.$numberOfOptions != 2 &&
		css`
			.Options:nth-last-child(2) {
				margin-top: ${baseDistance.sm};
				border-top: 1px solid ${colorPalette.gray.c300};
				border-radius: 0;
			}
		`}
`;

const ListElementContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	overflow-x: hidden;
	overflow-y: auto;
	gap: ${baseDistance.xs};

	.onlyFavoritesToggler {
		padding: 10px 0;

		.DetailsContentToggleSwitch {
			transform: scale(0.8);
		}
	}

	.favorite {
		justify-content: left;
		padding-left: ${baseDistance.sm};
	}
`;

const SearchWrapper = styled.div`
	${FlexCenterStyle};
	gap: ${baseDistance.sm};
	margin-bottom: ${baseDistance.sm};
`;

const OptionStyled = styled.div<{$optionFontSize?: string; $type?: string}>`
	${FlexCenterStyle};
	padding: ${(props) => (props.$type ? "" : "0 8px")};
	cursor: pointer;
	min-height: 32px;
	border-radius: ${radius.sm};
	font-size: ${(props) => props.$optionFontSize || fontSize.lg};

	span {
		&:has(div) {
			${FlexCenterStyle};
		}

		line-height: 18px;
		flex: 1;
		min-width: 0;
		${ELLIPSIS}

		${PillStyled} {
			${ELLIPSIS}
			display: inline-block;
			height: auto;
		}
	}

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.selected {
		border: none;
		background-color: ${colorPalette.primary.c200Light};
		color: ${colorPalette.primary.c500Primary};
	}

	&.usePillStyle {
		min-height: 40px;
		border-radius: ${radius.sm};
		box-sizing: border-box;
	}

	&:has(.nullLabel) {
		justify-content: center;
	}

	.fieldOption {
		width: 100%;
		justify-content: space-between;
	}

	.nullLabel {
		margin-left: ${baseDistance.xs};
	}

	svg {
		flex-shrink: 0;
	}

	${(props) => {
		switch (props.$type) {
			case "textOnly":
			case "pillColored":
			case "pill":
			case "flag":
			case "colorBar":
			case "colorBarFill":
				return css`
					&.selected {
						background-color: ${colorPalette.primary.c200Light};
						border: 2px solid ${colorPalette.primary.c500Primary};
						color: ${colorPalette.black};
					}
				`;
			case "default":
				return css`
					&.selected {
						background-color: ${colorPalette.primary.c200Light};
						border: 2px solid ${colorPalette.primary.c500Primary};
					}

					.nullLabel {
						${FLEXCENTER};
						height: 32px;
						margin: ${baseDistance.xs} ${baseDistance.xs};
						width: 100%;
						border-radius: ${radius.sm};
						border: 1px solid ${colorPalette.gray.c300};
						background-color: ${colorPalette.white};
					}
				`;
		}
	}}
`;

const SpanWithFlex1 = styled.span`
	flex: 1;
	min-width: 0;

	span {
		${ELLIPSIS};
	}
`;
